import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { UserPlusIcon, ArrowPathIcon, CheckIcon, ArrowsPointingOutIcon, ArrowsPointingInIcon, ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import { useProjects } from '../context/ProjectsContext';
import { projectService } from '../services/projectService';
import { devCapacityService } from '../services/devCapacityService';
import { getWeekRanges } from '../utils/dateUtils';
// TODO(PT-XXX permission ticket): wire export behind a permission.
// import { usePermissions } from '../hooks/usePermissions';
// import { PERMISSIONS } from '../constants/permissions';
import DevCapacityTable from './DevCapacityTable';
import AddDeveloperModal from './common/devCapacity/AddDeveloperModal';
import ProjectWorklogExportModal from './projects/ProjectWorklogExportModal';

export default function DevCapacity() {
  const navigate = useNavigate();
  const { id: projectId } = useParams();
  const { getProjectById } = useProjects();
  const contextProject = getProjectById(projectId);
  const [fetchedProject, setFetchedProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showAddDeveloperModal, setShowAddDeveloperModal] = useState(false);
  const [developerToEdit, setDeveloperToEdit] = useState(null);
  const [resetVersions, setResetVersions] = useState({});
  const [rawDevelopers, setRawDevelopers] = useState([]);
  const [pendingChanges, setPendingChanges] = useState({});
  const [removingDeveloper, setRemovingDeveloper] = useState(false);
  const [roleTypes, setRoleTypes] = useState([]);
  const [roles, setRoles] = useState([]);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);

  // TODO(PT-XXX permission ticket): gate the export button.
  // const { can } = usePermissions();
  // const canExportWorklogs = can(PERMISSIONS.PROJECT_WORKLOG_EXPORT);
  // For now the button is visible to everyone:
  const canExportWorklogs = true;

  useEffect(() => {
    if (isFullScreen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isFullScreen]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') setIsFullScreen(false);
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  const originalDevelopersRef = useRef({});

  const project = contextProject || fetchedProject;
  const weeks = useMemo(() => getWeekRanges(project), [project?.start_date, project?.end_date]);

  const hasUnsavedChanges = Object.keys(pendingChanges).length > 0;

  // Transform raw API data to frontend format (reactive to weeks changes)
  const developers = useMemo(() => {
    if (!weeks.length || !rawDevelopers.length) return [];

    return rawDevelopers.map((pd) => {
      const weeklyHours = weeks.map((week) => {
        const capacity = pd.weekly_capacities?.find((wc) => wc.week_start_date === week.start_date);
        const rawHours = parseFloat(capacity?.hours ?? 0);
        return isNaN(rawHours) ? 0 : parseFloat(rawHours.toFixed(2));
      });
      return {
        id: pd.id,
        developer_id: pd.developer_id,
        jira_account_id: pd.jira_account_id,
        name: pd.name,
        avatar_url: pd.avatar_url,
        role: pd.role?.name ?? '',
        role_type: pd.role_type?.name ?? '',
        weeklyHours,
      };
    });
  }, [rawDevelopers, weeks]);

  // Update original values for newly added developers
  useEffect(() => {
    developers.forEach((d) => {
      if (!originalDevelopersRef.current[d.id]) {
        originalDevelopersRef.current[d.id] = [...d.weeklyHours];
      }
    });
  }, [developers]);

  // Fetch project if not in context, then fetch developers and role options
  useEffect(() => {
    if (!projectId) return;
    let cancelled = false;

    const loadData = async () => {
      setLoading(true);
      try {
        let projectData = contextProject;

        if (!projectData) {
          projectData = await projectService.getProject(projectId);
          if (!cancelled) {
            setFetchedProject(projectData);
          }
        }

        const [developersResult, roleOptionsResult] = await Promise.allSettled([
          devCapacityService.getProjectDevelopers(projectId),
          devCapacityService.getRoleOptions(projectId),
        ]);

        if (cancelled) return;

        setPendingChanges({});
        originalDevelopersRef.current = {};

        if (developersResult.status === 'fulfilled') {
          setRawDevelopers(developersResult.value);
        } else {
          setRawDevelopers([]);
          toast.error(developersResult.reason?.message || 'Failed to fetch team members');
        }

        if (roleOptionsResult.status === 'fulfilled') {
          setRoleTypes(roleOptionsResult.value?.role_types ?? []);
          setRoles(roleOptionsResult.value?.roles ?? []);
        } else {
          setRoleTypes([]);
          setRoles([]);
          toast.error(roleOptionsResult.reason?.message || 'Failed to fetch role options');
        }
      } catch (err) {
        if (!cancelled) toast.error(err.message || 'Failed to load data');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadData();
    return () => { cancelled = true; };
  }, [projectId, contextProject]);

  const displayName = project?.name;

  // Calculate totals for the project by week and total project capacity
  const totals = useMemo(() => {
    const perWeek = weeks.map((_, wi) =>
      developers.reduce((sum, d) => sum + (d.weeklyHours?.[wi] ?? 0), 0)
    );
    const totalProjectCapacity = developers.reduce(
      (sum, d) => sum + (d.weeklyHours ?? []).reduce((a, b) => a + (b ?? 0), 0),
      0
    );
    // Only Dev and QA have dedicated breakdown rows.
    // Other role types (e.g. 'Other') are intentionally excluded from the breakdown
    // but are still included in the Total Capacity row.
    const devDevelopers = developers.filter((d) => d.role_type?.toLowerCase() === 'dev');
    const qaDevelopers = developers.filter((d) => d.role_type?.toLowerCase() === 'qa');

    const devPerWeek = weeks.map((_, wi) =>
      devDevelopers.reduce((sum, d) => sum + (d.weeklyHours?.[wi] ?? 0), 0)
    );
    const qaPerWeek = weeks.map((_, wi) =>
      qaDevelopers.reduce((sum, d) => sum + (d.weeklyHours?.[wi] ?? 0), 0)
    );

    const totalDevCapacity = devDevelopers.reduce(
      (sum, d) => sum + (d.weeklyHours ?? []).reduce((a, b) => a + (b ?? 0), 0),
      0
    );
    const totalQaCapacity = qaDevelopers.reduce(
      (sum, d) => sum + (d.weeklyHours ?? []).reduce((a, b) => a + (b ?? 0), 0),
      0
    );

    return { perWeek, totalProjectCapacity, devPerWeek, qaPerWeek, totalDevCapacity, totalQaCapacity };
  }, [developers, weeks]);
  // Add a new developer to the project
  const handleAddDeveloper = async (developerData) => {
    setSaving(true);
    try {
      const weekStartDates = weeks.map((w) => w.start_date);
      const response = await devCapacityService.addDeveloper(projectId, {
        jira_account_id: developerData.id,
        name: developerData.name,
        avatar_url: developerData.avatar_url,
        role_id: developerData.roleId,
        weekly_hours: developerData.weeklyHours,
        weeks: weekStartDates,
        max_hours_per_week: developerData.maxHoursPerWeek,
      });

      const responseWithCorrectHours = {
        ...response,
        weekly_capacities: weekStartDates.map((startDate, i) => ({
          ...(response.weekly_capacities?.find(wc => wc.week_start_date === startDate) ?? {}),
          week_start_date: startDate,
          hours: developerData.weeklyHours[i],
        })),
      };
      setRawDevelopers((prev) => [...prev, responseWithCorrectHours]);
      setShowAddDeveloperModal(false);
      toast.success('Team member added successfully');
    } catch (err) {
      toast.error(err.message || 'Failed to add team member');
    } finally {
      setSaving(false);
    }
  };

  // Remove a developer from the project
  const handleRemoveDeveloper = async (projectDeveloperId) => {
    setRemovingDeveloper(true);
    try {
      await devCapacityService.removeDeveloper(projectId, projectDeveloperId);
      setRawDevelopers((prev) => prev.filter((d) => d.id !== projectDeveloperId));
      delete originalDevelopersRef.current[projectDeveloperId];
      setPendingChanges((prev) => {
        const next = { ...prev };
        delete next[projectDeveloperId];
        return next;
      });
      toast.success('Team member removed');
    } catch (err) {
      toast.error(err.message || 'Failed to remove team member');
    } finally {
      setRemovingDeveloper(false);
    }
  };

  const handleEditDeveloper = useCallback((dev) => {
    const raw = rawDevelopers.find((d) => d.id === dev.id);
    if (!raw) return;
    setDeveloperToEdit({
      id: raw.id,
      name: raw.name,
      avatar_url: raw.avatar_url,
      jira_account_id: raw.jira_account_id,
      role_id: raw.role?.id ?? null,
      role_type_id: raw.role_type?.id ?? null,
      max_hours_per_week: raw.max_hours_per_week ?? null,
    });
  }, [rawDevelopers]);

  const handleSaveEdit = async ({ projectDeveloperId, weeklyHours, maxHoursPerWeek }) => {
    setSaving(true);
    try {
      const weekStartDates = weeks.map((w) => w.start_date);
      const response = await devCapacityService.updateDeveloper(projectId, projectDeveloperId, {
        maxHoursPerWeek,
        weeklyHours,
        weeks: weekStartDates,
      });

      setRawDevelopers((prev) => prev.map((d) => (d.id === projectDeveloperId ? response : d)));
      originalDevelopersRef.current[projectDeveloperId] = [...weeklyHours];
      setPendingChanges((prev) => {
        const next = { ...prev };
        delete next[projectDeveloperId];
        return next;
      });
      setResetVersions((prev) => ({ ...prev, [projectDeveloperId]: (prev[projectDeveloperId] ?? 0) + 1 }));

      setDeveloperToEdit(null);
      toast.success('Team member updated');
    } catch (err) {
      toast.error(err.message || 'Failed to update team member');
    } finally {
      setSaving(false);
    }
  };
  // Update the capacity of a developer for a specific week
  const handleCapacityChange = useCallback((projectDeveloperId, weekIndex, hours) => {
    const weekStartDate = weeks[weekIndex]?.start_date;
    if (!weekStartDate) return;
    setRawDevelopers((prev) =>
      prev.map((d) => {
        if (d.id !== projectDeveloperId) return d;
        const newCapacities = [...(d.weekly_capacities || [])];

        const existingIndex = newCapacities.findIndex((wc) => wc.week_start_date === weekStartDate);

        if (existingIndex >= 0) {
          newCapacities[existingIndex] = { ...newCapacities[existingIndex], hours };
        } else {
          newCapacities.push({ week_start_date: weekStartDate, hours });
        }

        return { ...d, weekly_capacities: newCapacities };
      })
    );

    setPendingChanges((prev) => {
      const original = originalDevelopersRef.current[projectDeveloperId];
      const originalHours = original?.[weekIndex] ?? 0;

      const devChanges = { ...(prev[projectDeveloperId] || {}) };

      if (hours !== originalHours) {
        devChanges[weekStartDate] = hours;
      } else {
        delete devChanges[weekStartDate];
      }

      if (Object.keys(devChanges).length === 0) {
        const next = { ...prev };
        delete next[projectDeveloperId];
        return next;
      }

      return { ...prev, [projectDeveloperId]: devChanges };
    });
  }, [weeks]);

  // Save only the changed weeks for each modified developer
  const handleSaveChanges = async () => {
    if (!hasUnsavedChanges) return;

    setSaving(true);
    try {
      const promises = Object.entries(pendingChanges).map(([devId, weekChanges]) => {
        const changedWeeks = Object.keys(weekChanges);
        const changedHours = changedWeeks.map((week) => weekChanges[week]);
        return devCapacityService.updateCapacities(projectId, Number(devId), changedHours, changedWeeks);
      });

      await Promise.all(promises);

      Object.keys(pendingChanges).forEach((devId) => {
        const developer = developers.find((d) => d.id === Number(devId));
        if (developer) {
          originalDevelopersRef.current[devId] = [...developer.weeklyHours];
        }
      });
      setPendingChanges({});
      toast.success('Changes saved successfully');
    } catch (err) {
      toast.error(err.message || 'Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={isFullScreen ? 'fixed inset-0 z-50 bg-white dark:bg-slate-900 p-4 flex flex-col overflow-hidden' : 'p-6'}>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3 relative z-20">
        <div className="flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={() => navigate('/projects')}
            className="flex items-center gap-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-200 shadow-sm hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
          >
            ← Back to Projects
          </button>
          {displayName ? (
            <span className="text-slate-600 dark:text-slate-400 text-sm">
              Team capacity for <strong className="text-slate-800 dark:text-slate-200">{displayName}</strong>
            </span>
          ) : loading ? (
            <span className="text-slate-500 dark:text-slate-400 text-sm">Loading...</span>
          ) : null}
        </div>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={handleSaveChanges}
            disabled={!hasUnsavedChanges || saving}
            className={`inline-flex items-center gap-2 rounded-lg border px-4 py-2 text-sm font-medium transition-colors ${hasUnsavedChanges
              ? 'border-indigo-300 bg-indigo-50 text-indigo-700 hover:bg-indigo-100 dark:border-indigo-600 dark:bg-indigo-900/30 dark:text-indigo-300 dark:hover:bg-indigo-900/50'
              : 'border-slate-300 bg-slate-100 text-slate-400 cursor-not-allowed dark:border-slate-600 dark:bg-slate-700 dark:text-slate-500'
              } ${saving ? 'opacity-70 cursor-wait' : ''}`}
          >
            {saving ? (
              <>
                <ArrowPathIcon className="size-4 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <CheckIcon className="size-4" />
                Save Changes
              </>
            )}
          </button>
          <button
            type="button"
            onClick={() => setIsFullScreen(prev => !prev)}
            className="inline-flex items-center gap-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
            title={isFullScreen ? 'Exit full screen' : 'Expand to full screen'}
          >
            {isFullScreen
              ? <ArrowsPointingInIcon className="size-4" />
              : <ArrowsPointingOutIcon className="size-4" />
            }
            {isFullScreen ? 'Exit Full Screen' : 'Full Screen'}
          </button>
          {canExportWorklogs && (
            <button
              type="button"
              className="inline-flex items-center gap-2 rounded-lg border border-emerald-300 dark:border-emerald-600 bg-emerald-50 dark:bg-emerald-900/30 px-4 py-2 text-sm font-medium text-emerald-700 dark:text-emerald-300 hover:bg-emerald-100 dark:hover:bg-emerald-900/50 transition-colors"
              onClick={() => setShowExportModal(true)}
            >
              <ArrowDownTrayIcon className="size-4" />
              Download Worklogs
            </button>
          )}
          <button
            type="button"
            className="inline-flex items-center gap-2 rounded-lg border border-indigo-300 dark:border-indigo-600 bg-indigo-50 dark:bg-indigo-900/30 px-4 py-2 text-sm font-medium text-indigo-700 dark:text-indigo-300 hover:bg-indigo-100 dark:hover:bg-indigo-900/50 transition-colors"
            onClick={() => setShowAddDeveloperModal(true)}
          >
            <UserPlusIcon className="size-4" />
            Add team member
          </button>
        </div>
      </div>
      <div className={isFullScreen ? 'flex-1 min-h-0 overflow-hidden' : ''}>
        <DevCapacityTable
          developers={developers}
          weeks={weeks}
          totals={totals}
          onRemove={handleRemoveDeveloper}
          onEdit={handleEditDeveloper}
          onCapacityChange={handleCapacityChange}
          removing={removingDeveloper}
          isFullScreen={isFullScreen}
          resetVersions={resetVersions}
        />
      </div>
      {loading && (
        <div className="mt-6 flex items-center justify-center gap-2 text-slate-500 dark:text-slate-400">
          <ArrowPathIcon className="size-5 animate-spin" />
          <span className="text-sm">Loading team members...</span>
        </div>
      )}

      {showAddDeveloperModal && (
        <AddDeveloperModal
          onClose={() => setShowAddDeveloperModal(false)}
          onSubmit={handleAddDeveloper}
          loading={saving}
          projectKey={project?.key || project?.jira_project_key}
          weekCount={weeks.length}
          weeks={weeks}
          roleTypes={roleTypes}
          roles={roles}
        />
      )}

      {showExportModal && (
        <ProjectWorklogExportModal
          projectId={projectId}
          projectName={displayName}
          projectKey={project?.jira_project_key || project?.key}
          onClose={() => setShowExportModal(false)}
        />
      )}

      {developerToEdit && (
        <AddDeveloperModal
          mode="edit"
          initialData={developerToEdit}
          onClose={() => setDeveloperToEdit(null)}
          onSubmit={handleSaveEdit}
          loading={saving}
          weekCount={weeks.length}
          weeks={weeks}
          roleTypes={roleTypes}
          roles={roles}
        />
      )}
    </div>
  );
}
